package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.data.model.*
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import co.uk.sacursoftware.ssinventory.util.LocalUserRole
import co.uk.sacursoftware.ssinventory.util.UserRole
import kotlinx.coroutines.launch
import androidx.compose.foundation.Image
import androidx.compose.ui.layout.ContentScale
import org.jetbrains.compose.resources.painterResource
import ssinventory.shared.generated.resources.*

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun TrainingScreen(
    onBack: () -> Unit,
    onNavigateToAddEdit: (TrainingItem?) -> Unit,
    onNavigateToCategoryManager: () -> Unit,
    onNavigateToImageViewer: (List<String>, Int, String?) -> Unit
) {
    val scope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }
    val uriHandler = LocalUriHandler.current
    val isAdmin = LocalUserRole.current == UserRole.ADMIN

    var trainingItems by remember { mutableStateOf<List<TrainingItem>>(emptyList()) }
    var categories by remember { mutableStateOf<List<TrainingCategory>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }

    // Folder navigation state
    var selectedCategory by remember { mutableStateOf<TrainingCategory?>(null) }
    var selectedSubCategory by remember { mutableStateOf<String?>(null) }
    var searchQuery by remember { mutableStateOf("") }
    var viewingItemDetails by remember { mutableStateOf<TrainingItem?>(null) }
    var itemToDelete by remember { mutableStateOf<TrainingItem?>(null) }
    var showAddBottomSheet by remember { mutableStateOf(false) }
    var showAddFolderDialog by remember { mutableStateOf(false) }
    var newFolderName by remember { mutableStateOf("") }

    fun loadData() {
        scope.launch {
            isLoading = true
            repository.getTrainingCategories().onSuccess { cats ->
                categories = cats
            }
            repository.getTrainingItems().onSuccess { items ->
                trainingItems = items
            }
            isLoading = false
        }
    }

    LaunchedEffect(Unit) {
        loadData()
    }

    // Filter items based on selected category, subcategory, and search query
    val filteredItems = trainingItems.filter { item ->
        val matchesSearch = searchQuery.isBlank() || 
            item.title.contains(searchQuery, ignoreCase = true) || 
            (item.description?.contains(searchQuery, ignoreCase = true) == true)
        
        val matchesCategory = selectedCategory == null || item.categoryId == selectedCategory?.id
        val matchesSubCategory = selectedSubCategory == null || item.subCategoryName.equals(selectedSubCategory, ignoreCase = true)

        matchesSearch && matchesCategory && matchesSubCategory
    }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            TopAppBar(
                title = { 
                    Text(
                        when {
                            selectedSubCategory != null -> "Training: $selectedSubCategory"
                            selectedCategory != null -> "Training: ${selectedCategory?.categoryName}"
                            else -> "Training & Resources"
                        }
                    ) 
                },
                navigationIcon = {
                    IconButton(onClick = {
                        when {
                            selectedSubCategory != null -> selectedSubCategory = null
                            selectedCategory != null -> selectedCategory = null
                            else -> onBack()
                        }
                    }) {
                        BackArrowIcon()
                    }
                },
                actions = {
                    if (isAdmin) {
                        ToolbarPlusButton(onClick = { showAddBottomSheet = true })
                    }
                }
            )
        }
    ) { padding ->
        Box(modifier = Modifier.padding(padding).fillMaxSize()) {
            if (showAddBottomSheet) {
                ModalBottomSheet(
                    onDismissRequest = { showAddBottomSheet = false }
                ) {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(24.dp),
                        verticalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        Text("Add New", style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Bold)
                        
                        ListItem(
                            headlineContent = { Text("Folder", fontWeight = FontWeight.Bold) },
                            supportingContent = { Text(selectedCategory?.let { "Create subfolder under ${it.categoryName}" } ?: "Create root folder") },
                            modifier = Modifier.clickable {
                                showAddBottomSheet = false
                                showAddFolderDialog = true
                            }
                        )
                        HorizontalDivider()
                        ListItem(
                            headlineContent = { Text("Training Item", fontWeight = FontWeight.Bold) },
                            supportingContent = { Text("Add video, PDF, or image training resource") },
                            modifier = Modifier.clickable {
                                showAddBottomSheet = false
                                onNavigateToAddEdit(null)
                            }
                        )
                        Spacer(modifier = Modifier.height(24.dp))
                    }
                }
            }

            if (showAddFolderDialog) {
                AlertDialog(
                    onDismissRequest = { showAddFolderDialog = false },
                    title = { Text(selectedCategory?.let { "Create Subfolder under ${it.categoryName}" } ?: "Create New Folder") },
                    text = {
                        OutlinedTextField(
                            value = newFolderName,
                            onValueChange = { newFolderName = it },
                            label = { Text("Folder Name *") },
                            singleLine = true,
                            shape = MaterialTheme.shapes.small,
                            modifier = Modifier.fillMaxWidth()
                        )
                    },
                    confirmButton = {
                        Button(
                            onClick = {
                                if (newFolderName.isNotBlank()) {
                                    val folderName = newFolderName.trim()
                                    val parentCatName = selectedCategory?.categoryName
                                    val req = SaveTrainingCategoryRequest(
                                        categoryName = parentCatName ?: folderName,
                                        subCategoryName = if (parentCatName != null) folderName else null
                                    )
                                    scope.launch {
                                        repository.saveTrainingCategory(req).onSuccess {
                                            newFolderName = ""
                                            showAddFolderDialog = false
                                            loadData()
                                            snackbarHostState.showSnackbar("Folder created successfully")
                                        }
                                    }
                                }
                            },
                            enabled = newFolderName.isNotBlank()
                        ) {
                            Text("Create")
                        }
                    },
                    dismissButton = {
                        TextButton(onClick = { showAddFolderDialog = false }) {
                            Text("Cancel")
                        }
                    },
                    shape = MaterialTheme.shapes.large
                )
            }
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                // Search Bar
                OutlinedTextField(
                    value = searchQuery,
                    onValueChange = { searchQuery = it },
                    label = { Text("Search training & resources...") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    shape = MaterialTheme.shapes.small,
                    trailingIcon = {
                        if (searchQuery.isNotEmpty()) {
                            TextButton(onClick = { searchQuery = "" }) { Text("Clear") }
                        }
                    }
                )

                if (isLoading) {
                    Box(modifier = Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) {
                        BrandLoadingSpinner()
                    }
                } else if (searchQuery.isBlank() && selectedCategory == null) {
                    // Level 0: Display Category Folders
                    val distinctCategories = categories.distinctBy { it.categoryName }
                    if (distinctCategories.isEmpty()) {
                        Box(modifier = Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) {
                            Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(8.dp)) {
                                Text("No training categories found.", style = MaterialTheme.typography.bodyLarge, color = MaterialTheme.colorScheme.outline)
                                if (isAdmin) {
                                    Button(onClick = onNavigateToCategoryManager) {
                                        Text("Create Categories")
                                    }
                                }
                            }
                        }
                    } else {
                        Text("Select Category", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
                        LazyColumn(
                            modifier = Modifier.fillMaxWidth().weight(1f),
                            verticalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            items(items = distinctCategories, key = { it.id }) { cat ->
                                val itemCount = trainingItems.count { it.categoryId == cat.id || it.categoryName.equals(cat.categoryName, ignoreCase = true) }
                                Card(
                                    modifier = Modifier.fillMaxWidth().clickable {
                                        selectedCategory = cat
                                        selectedSubCategory = null
                                    },
                                    shape = MaterialTheme.shapes.medium,
                                    colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant),
                                    border = BorderStroke(1.dp, MaterialTheme.colorScheme.outline.copy(alpha = 0.3f))
                                ) {
                                    Row(
                                        modifier = Modifier.fillMaxWidth().padding(16.dp),
                                        horizontalArrangement = Arrangement.SpaceBetween,
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        Row(
                                            horizontalArrangement = Arrangement.spacedBy(12.dp),
                                            verticalAlignment = Alignment.CenterVertically
                                        ) {
                                            Image(
                                                painter = painterResource(Res.drawable.folder),
                                                contentDescription = "Folder",
                                                modifier = Modifier.size(24.dp),
                                                contentScale = ContentScale.Fit
                                            )
                                            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                                                Text(
                                                    text = cat.categoryName,
                                                    style = MaterialTheme.typography.titleMedium,
                                                    fontWeight = FontWeight.Bold
                                                )
                                                Text(
                                                    text = "$itemCount training item(s)",
                                                    style = MaterialTheme.typography.bodySmall,
                                                    color = MaterialTheme.colorScheme.primary
                                                )
                                            }
                                        }
                                        Text(">", style = MaterialTheme.typography.titleLarge, color = MaterialTheme.colorScheme.outline)
                                    }
                                }
                            }
                        }
                    }
                } else if (searchQuery.isBlank() && selectedCategory != null && selectedSubCategory == null) {
                    // Level 1: Display Subcategories or Items for selected Category
                    val subCategories = categories.filter { it.categoryName.equals(selectedCategory?.categoryName, ignoreCase = true) && !it.subCategoryName.isNullOrBlank() }.mapNotNull { it.subCategoryName }
                    
                    if (subCategories.isNotEmpty()) {
                        Text("Subcategories in ${selectedCategory?.categoryName}", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
                        LazyColumn(
                            modifier = Modifier.fillMaxWidth().weight(1f),
                            verticalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            items(items = subCategories, key = { it }) { sub ->
                                Card(
                                    modifier = Modifier.fillMaxWidth().clickable {
                                        selectedSubCategory = sub
                                    },
                                    shape = MaterialTheme.shapes.medium,
                                    colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant),
                                    border = BorderStroke(1.dp, MaterialTheme.colorScheme.outline.copy(alpha = 0.3f))
                                ) {
                                    Row(
                                        modifier = Modifier.fillMaxWidth().padding(16.dp),
                                        horizontalArrangement = Arrangement.SpaceBetween,
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        Row(
                                            horizontalArrangement = Arrangement.spacedBy(12.dp),
                                            verticalAlignment = Alignment.CenterVertically
                                        ) {
                                            Image(
                                                painter = painterResource(Res.drawable.folder),
                                                contentDescription = "Folder",
                                                modifier = Modifier.size(24.dp),
                                                contentScale = ContentScale.Fit
                                            )
                                            Text(
                                                text = sub,
                                                style = MaterialTheme.typography.titleMedium,
                                                fontWeight = FontWeight.Bold
                                            )
                                        }
                                        Text(">", style = MaterialTheme.typography.titleLarge, color = MaterialTheme.colorScheme.outline)
                                    }
                                }
                            }
                        }
                    } else {
                        // Show items directly
                        TrainingItemsList(
                            items = filteredItems,
                            isAdmin = isAdmin,
                            onItemClick = { viewingItemDetails = it },
                            onEdit = { onNavigateToAddEdit(it) },
                            onDelete = { itemToDelete = it },
                            onViewImages = onNavigateToImageViewer
                        )
                    }
                } else {
                    // Level 2: Display Training Items List
                    TrainingItemsList(
                        items = filteredItems,
                        isAdmin = isAdmin,
                        onItemClick = { viewingItemDetails = it },
                        onEdit = { onNavigateToAddEdit(it) },
                        onDelete = { itemToDelete = it },
                        onViewImages = onNavigateToImageViewer
                    )
                }
            }

            // Item Details Dialog
            viewingItemDetails?.let { item ->
                AlertDialog(
                    onDismissRequest = { viewingItemDetails = null },
                    title = { Text(item.title) },
                    text = {
                        Column(
                            modifier = Modifier.fillMaxWidth().verticalScroll(rememberScrollState()),
                            verticalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            if (!item.categoryName.isNullOrBlank()) {
                                Text("Category: ${item.categoryName}${item.subCategoryName?.let { " -> $it" } ?: ""}", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.secondary)
                            }
                            if (!item.description.isNullOrBlank()) {
                                Text(item.description, style = MaterialTheme.typography.bodyMedium)
                            }
                            if (!item.images.isNullOrEmpty()) {
                                Text("Attachments (${item.images.size})", style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold)
                                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                                    item.images.forEach { url ->
                                        if (isImageFileUrl(url)) {
                                            Box(
                                                modifier = Modifier.size(80.dp).clickable {
                                                    viewingItemDetails = null
                                                    val images = item.images.filter { isImageFileUrl(it) }
                                                    onNavigateToImageViewer(images, images.indexOf(url).coerceAtLeast(0), item.title)
                                                }
                                            ) {
                                                RemoteImageView(url = url, modifier = Modifier.fillMaxSize())
                                            }
                                        } else {
                                            TextButton(onClick = {
                                                viewingItemDetails = null
                                                runCatching { uriHandler.openUri(url) }
                                            }) {
                                                Text(displayFileName(url), color = MaterialTheme.colorScheme.primary)
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    },
                    confirmButton = {
                        Button(onClick = { viewingItemDetails = null }) {
                            Text("Close")
                        }
                    },
                    shape = MaterialTheme.shapes.large
                )
            }

            // Delete Confirmation Dialog
            itemToDelete?.let { item ->
                AlertDialog(
                    onDismissRequest = { itemToDelete = null },
                    title = { Text("Delete Training Item") },
                    text = { Text("Are you sure you want to delete '${item.title}'?") },
                    confirmButton = {
                        Button(
                            onClick = {
                                val tId = item.id
                                itemToDelete = null
                                trainingItems = trainingItems.filter { it.id != tId }
                                scope.launch {
                                    repository.deleteTrainingItem(tId)
                                }
                            },
                            colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.error)
                        ) {
                            Text("Delete")
                        }
                    },
                    dismissButton = {
                        TextButton(onClick = { itemToDelete = null }) {
                            Text("Cancel")
                        }
                    },
                    shape = MaterialTheme.shapes.large
                )
            }
        }
    }
}

@Composable
fun TrainingItemsList(
    items: List<TrainingItem>,
    isAdmin: Boolean,
    onItemClick: (TrainingItem) -> Unit,
    onEdit: (TrainingItem) -> Unit,
    onDelete: (TrainingItem) -> Unit,
    onViewImages: (List<String>, Int, String?) -> Unit
) {
    if (items.isEmpty()) {
        Box(modifier = Modifier.fillMaxWidth().fillMaxHeight(), contentAlignment = Alignment.Center) {
            Text("No training items found.", style = MaterialTheme.typography.bodyLarge, color = MaterialTheme.colorScheme.outline)
        }
    } else {
        LazyColumn(
            modifier = Modifier.fillMaxWidth().fillMaxHeight(),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            items(items = items, key = { it.id }) { item ->
                Card(
                    modifier = Modifier.fillMaxWidth().clickable { onItemClick(item) },
                    shape = MaterialTheme.shapes.medium,
                    colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                    border = BorderStroke(1.dp, MaterialTheme.colorScheme.outline.copy(alpha = 0.3f))
                ) {
                    Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text(
                                text = item.title,
                                style = MaterialTheme.typography.titleMedium,
                                fontWeight = FontWeight.Bold,
                                color = MaterialTheme.colorScheme.onSurface,
                                modifier = Modifier.weight(1f)
                            )
                            if (isAdmin) {
                                Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                                    TextButton(onClick = { onEdit(item) }) { Text("Edit") }
                                    IconButton(onClick = { onDelete(item) }) {
                                        CloseIcon(tint = MaterialTheme.colorScheme.error)
                                    }
                                }
                            }
                        }

                        if (!item.categoryName.isNullOrBlank()) {
                            Text(
                                text = "${item.categoryName}${item.subCategoryName?.let { " -> $it" } ?: ""}",
                                style = MaterialTheme.typography.bodySmall,
                                fontWeight = FontWeight.SemiBold,
                                color = MaterialTheme.colorScheme.primary
                            )
                        }

                        if (!item.description.isNullOrBlank()) {
                            Text(
                                text = item.description,
                                style = MaterialTheme.typography.bodyMedium,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                maxLines = 2
                            )
                        }

                        if (!item.images.isNullOrEmpty()) {
                            val photos = item.images.filter { isImageFileUrl(it) }
                            val files = item.images.filter { !isImageFileUrl(it) }
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.End,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                if (photos.isNotEmpty()) {
                                    TextButton(onClick = { onViewImages(photos, 0, item.title) }) {
                                        Text("${photos.size} photo(s)")
                                    }
                                }
                                if (files.isNotEmpty()) {
                                    Text("${files.size} file(s)", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.primary)
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
